// This module is the DEFAULT SITE TEMPLATE — the content every tenant renders until it
// saves its own. A default is therefore, by construction, the wrong place for any real
// person's identity: whatever sits here is published on every new practitioner's live
// public site, under their name. Two separate harms follow from getting that wrong — the
// new practitioner's site states things about them that are false, and the individual
// whose details were used has their personal data disclosed to strangers.
//
// The rule for this file: nothing here may identify, or be contactable at, a real person
// or business. No real names, photographs, logos, addresses, phone numbers, email
// addresses, social profiles or review links. Placeholder contact details use the ranges
// reserved for exactly this purpose so that they cannot reach anyone even if a tenant
// publishes without editing: example.com (RFC 2606) and +1 555-01xx (NANP fictional).
//
// Real tenant data reaches the site through the tenant record (`tenant.name`,
// `tenant.branding.logo_url`) and through `content_blocks` overrides, which
// `templates/shared/useSiteData.js` layers on top of these defaults.

/** Inline SVG placeholder → data URI. Keeps the neutral defaults dependency-free: no new
 *  binary asset to ship, and no request that can 404 into a broken image. */
const svgDataUri = (svg) => `data:image/svg+xml,${encodeURIComponent(svg.replace(/\s+/g, " ").trim())}`;

/** Wordless mark. Deliberately carries no name — a logo that names anyone is a claim. */
const PLACEHOLDER_LOGO = svgDataUri(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" role="img">
    <rect width="128" height="128" rx="28" fill="#EFEDE6"/>
    <path d="M64 32c17 11 26 25 26 40a26 26 0 0 1-52 0c0-15 9-29 26-40z"
          fill="none" stroke="#8A9A8B" stroke-width="4" stroke-linejoin="round"/>
    <path d="M64 44v52" fill="none" stroke="#8A9A8B" stroke-width="4" stroke-linecap="round"/>
  </svg>
`);

/** Portrait slot. An abstract figure, never a photograph: the previous default was a
 *  photograph of a real identifiable person, shown as though she ran every new practice. */
const PLACEHOLDER_PORTRAIT = svgDataUri(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 1200" role="img" preserveAspectRatio="xMidYMid slice">
    <rect width="600" height="1200" fill="#EFEDE6"/>
    <rect x="24" y="24" width="552" height="1152" rx="24" fill="none"
          stroke="#CFCBBD" stroke-width="3" stroke-dasharray="14 12"/>
    <circle cx="300" cy="700" r="96" fill="#DCDACE"/>
    <path d="M132 1000c0-93 75-168 168-168s168 75 168 168v152H132z" fill="#DCDACE"/>
  </svg>
`);

export const ASSETS = {
  heroBg: "/site-assets/hero-bg.png",
  heroPortrait: PLACEHOLDER_PORTRAIT,
  logo: PLACEHOLDER_LOGO,
  mortar: "/site-assets/mortar.png",
  apothecary: "/site-assets/apothecary.png",
  hotStone: "/site-assets/hot-stone.png",
  handMassage: "/site-assets/hand-massage.png",
};

// Slots d'images éditables du site public. `key` = clé content_blocks ; `fallback` = asset par défaut.
export const SITE_IMAGE_SLOTS = [
  { key: "site.heroBg", fallback: ASSETS.heroBg, labelFr: "Arrière-plan principal (Hero)", labelEn: "Hero background", hintFr: "Image plein-écran de la section d'accueil", hintEn: "Full-screen hero background" },
  { key: "site.heroPortrait", fallback: ASSETS.heroPortrait, labelFr: "Hero · Portrait principal", labelEn: "Hero · Main portrait", hintFr: "Section d'accueil (~600×800)", hintEn: "Hero section (~600×800)" },
  { key: "site.logo", fallback: ASSETS.logo, labelFr: "Logo (en-tête + pied de page)", labelEn: "Logo (header + footer)", hintFr: "Format PNG transparent recommandé", hintEn: "Transparent PNG recommended" },
  { key: "site.mortar", fallback: ASSETS.mortar, labelFr: "Mon parcours · Mortier & pilon", labelEn: "My journey · Mortar & pestle", hintFr: "Colonne latérale (3:4)", hintEn: "Sidebar (3:4)" },
  { key: "site.apothecary", fallback: ASSETS.apothecary, labelFr: "Mon parcours · Apothicaire", labelEn: "My journey · Apothecary", hintFr: "Colonne latérale (4:3)", hintEn: "Sidebar (4:3)" },
  { key: "site.hotStone", fallback: ASSETS.hotStone, labelFr: "Approche · Pierres chaudes", labelEn: "Approach · Hot stones", hintFr: "Section approche (4:5)", hintEn: "Approach section (4:5)" },
  { key: "site.handMassage", fallback: ASSETS.handMassage, labelFr: "Services · Image en vedette", labelEn: "Services · Featured image", hintFr: "Carte service mise en avant", hintEn: "Featured service card" },
];

export const translations = {
  fr: {
    nav: {
      story: "Mon parcours",
      approach: "Approche",
      services: "Services",
      testimonials: "Témoignages",
      blog: "Journal",
      contact: "Contact",
      book: "Réserver un appel",
      account: "Mon espace",
      programs: "Programmes",
      pathways: "Parcours",
      shop: "Boutique",
      reserve: "Réserver",
      courses: "Cours",
    },
    hero: {
      // eyebrow/stat*/credential have NO dictionary default: they state facts about the
      // practitioner (title, city, region, years in practice, hours of training) that this
      // file cannot invent without publishing a false claim about whoever hasn't set them
      // yet (BUG-210). Empty here + the render guards in Hero.jsx / useSiteData.js is the
      // fix; see resolveSiteCopy in lib/siteContent.js for how a Site Content override
      // fills them back in.
      eyebrow: "",
      // The whole sample headline, a line per "\n" (the Classic hero sets line 2 in italic).
      title: "Un accompagnement\nvers votre\nmieux-être.",
      script: "résumez votre approche en quelques mots",
      sub: "Présentez ici votre pratique, vos spécialités et les personnes que vous accompagnez. Ce texte est un exemple : remplacez-le depuis votre tableau de bord.",
      cta: "Réserver un appel découverte",
      ctaSecondary: "Découvrir l'approche",
      stat1: "",
      stat1Label: "",
      stat2: "",
      stat2Label: "",
      stat3: "",
      stat3Label: "",
      credential: "",
    },
    story: {
      eyebrow: "Mon parcours",
      title: "Votre histoire,\nen quelques chapitres.",
      lead: "Racontez ici le chemin qui vous a menée à votre pratique. Ce texte est un exemple : remplacez-le depuis votre tableau de bord.",
      chapterWord: "chapitre",
      quote: "« Ajoutez ici une citation qui résume votre philosophie. »",
      chapters: [
        {
          n: "01",
          title: "Le point de départ",
          body: "Décrivez ce qui vous a menée vers votre discipline. Remplacez ce texte d'exemple.",
        },
        {
          n: "02",
          title: "La formation",
          body: "Présentez vos études, vos certifications et les personnes qui vous ont formée. Remplacez ce texte d'exemple.",
        },
        {
          n: "03",
          title: "Les premières années",
          body: "Racontez vos débuts en pratique et ce qu'ils vous ont appris. Remplacez ce texte d'exemple.",
        },
        {
          n: "04",
          title: "Le tournant",
          body: "Partagez l'expérience qui a façonné votre approche actuelle. Remplacez ce texte d'exemple.",
        },
        {
          n: "05",
          title: "Aujourd'hui",
          body: "Expliquez qui vous accompagnez aujourd'hui, et de quelle manière. Remplacez ce texte d'exemple.",
        },
      ],
    },
    approach: {
      eyebrow: "Mon approche",
      title: "Une approche\nqui vous ressemble.",
      lead: "Décrivez ici les principes qui guident votre pratique. Ce texte est un exemple : remplacez-le depuis votre tableau de bord.",
      pillars: [
        { title: "Premier domaine", body: "Nommez un domaine que vous couvrez et décrivez-le en une phrase." },
        { title: "Deuxième domaine", body: "Nommez un domaine que vous couvrez et décrivez-le en une phrase." },
        { title: "Troisième domaine", body: "Nommez un domaine que vous couvrez et décrivez-le en une phrase." },
        { title: "Quatrième domaine", body: "Nommez un domaine que vous couvrez et décrivez-le en une phrase." },
      ],
    },
    services: {
      eyebrow: "Services",
      title: "Un accompagnement\nfait pour vous.",
      lead: "Chaque rencontre est une conversation. Décrivez ici votre façon d'accompagner. Remplacez ce texte d'exemple.",
      items: [
        {
          tag: "Exemple",
          title: "Appel découverte",
          duration: "20 min",
          body: "Décrivez ce premier échange : ce que vous y abordez et ce que la personne en repart avec. Remplacez ce texte d'exemple.",
        },
        {
          tag: "Exemple",
          title: "Consultation initiale",
          duration: "75 min",
          body: "Décrivez le déroulé d'une première consultation complète. Remplacez ce texte d'exemple.",
        },
        {
          tag: "Exemple",
          title: "Consultation de suivi",
          duration: "45 min",
          body: "Décrivez la façon dont vous assurez le suivi dans le temps. Remplacez ce texte d'exemple.",
        },
      ],
      cta: "Voir les disponibilités",
    },
    testimonials: {
      eyebrow: "Témoignages",
      readOnGoogle: "Lire sur Google ↗",
      title: "Ce que disent\nvos clients.",
      items: [
        {
          quote: "Les témoignages que vous publierez depuis votre tableau de bord s'afficheront ici, à la place de cet exemple.",
          name: "Témoignage d'exemple",
        },
        {
          quote: "Ajoutez les mots de vos clients : ce sont eux qui rassurent le mieux une personne qui hésite encore.",
          name: "Témoignage d'exemple",
        },
        {
          quote: "Trois témoignages suffisent pour commencer. Vous pourrez en ajouter autant que vous le souhaitez.",
          name: "Témoignage d'exemple",
        },
      ],
    },
    blog: {
      eyebrow: "Le journal",
      title: "Votre journal,\nvos sujets.",
      lead: "Publiez vos articles depuis votre tableau de bord. Voici à quoi ils ressembleront.",
      items: [
        {
          tag: "Exemple",
          title: "Le titre de votre premier article",
          excerpt: "Les articles que vous publierez remplaceront ces exemples. Rédigez-les depuis la section Journal de votre tableau de bord.",
          date: "À paraître",
        },
        {
          tag: "Exemple",
          title: "Le titre de votre deuxième article",
          excerpt: "Un court résumé donne envie de lire la suite : deux phrases suffisent.",
          date: "À paraître",
        },
        {
          tag: "Exemple",
          title: "Le titre de votre troisième article",
          excerpt: "Écrivez sur les questions que vos clients vous posent le plus souvent.",
           date: "À paraître",
        },
      ],
      readMore: "Lire l'article",
      viewAll: "Voir tous les articles",
    },
    contact: {
      eyebrow: "Restons en contact",
      title: "Une question,\nun pas vers vous.",
      lead: "Laissez-moi un mot. Je réponds personnellement à chaque message, en général sous 48 h.",
      email: "Courriel",
      // The Contact section's form (ContactForm.jsx) fetches the practice's capture form
      // from the backend and draws every field label, button and error through the
      // server's own locale layer (`cfg.ui`, `modules/crm/locale.capture_widget_strings`)
      // so the embed and the site form can never say different things. This is the ONE
      // exception: shown when that fetch itself fails, before any `cfg` — and therefore
      // any `cfg.ui` — exists to read a string from.
      loadError: "Le formulaire de contact n'a pas pu être chargé. Écrivez-nous directement par courriel.",
      placeLabel: "Lieu",
      info: {
        phone: "+1 555-0100",
        email: "contact@example.com",
        place: "Votre ville, votre région",
      },
    },
    footer: {
      // No default either — same reasoning as hero.eyebrow/stat*/credential above.
      tagline: "",
      rights: "Tous droits réservés.",
      built: "Conçu avec soin.",
      navigate: "Naviguer",
      explore: "Découvrir",
      social: "Réseaux sociaux",
    },
    marquee: ["Bien-être", "Équilibre", "Vitalité", "Écoute", "Prévention", "Énergie", "Sommeil", "Sérénité"],
    chatbot: {
      ariaLabel: "Assistante virtuelle",
      title: "Assistante IA",
      subtitle: "Réponses en quelques secondes",
      placeholder: "Tapez votre question...",
      error: "Désolée, une erreur est survenue. Réessayez dans un instant.",
    },
  },
  en: {
    nav: {
      story: "My Journey",
      approach: "Approach",
      services: "Services",
      testimonials: "Stories",
      blog: "Journal",
      contact: "Contact",
      book: "Book a call",
      account: "My account",
      programs: "Programs",
      pathways: "Pathways",
      shop: "Shop",
      reserve: "Book",
      courses: "Courses",
    },
    hero: {
      // eyebrow/stat*/credential have NO dictionary default: they state facts about the
      // practitioner (title, city, region, years in practice, hours of training) that this
      // file cannot invent without publishing a false claim about whoever hasn't set them
      // yet (BUG-210). Empty here + the render guards in Hero.jsx / useSiteData.js is the
      // fix; see resolveSiteCopy in lib/siteContent.js for how a Site Content override
      // fills them back in.
      eyebrow: "",
      title: "Care that guides you\ntoward your\nwellbeing.",
      script: "sum up your approach in a few words",
      sub: "Introduce your practice, your specialties and the people you work with. This is sample text — replace it from your dashboard.",
      cta: "Book a discovery call",
      ctaSecondary: "Explore the approach",
      stat1: "",
      stat1Label: "",
      stat2: "",
      stat2Label: "",
      stat3: "",
      stat3Label: "",
      credential: "",
    },
    story: {
      eyebrow: "My Journey",
      title: "Your story,\nin a few chapters.",
      lead: "Tell the story of how you came to your practice. This is sample text — replace it from your dashboard.",
      chapterWord: "chapter",
      quote: "\"Add a quote here that captures your philosophy.\"",
      chapters: [
        { n: "01", title: "The starting point", body: "Describe what drew you to your discipline. Replace this sample text." },
        { n: "02", title: "Training", body: "Introduce your studies, your certifications and the people who taught you. Replace this sample text." },
        { n: "03", title: "The early years", body: "Tell us about your first years in practice and what they taught you. Replace this sample text." },
        { n: "04", title: "The turning point", body: "Share the experience that shaped the approach you use today. Replace this sample text." },
        { n: "05", title: "Today", body: "Explain who you work with today, and how. Replace this sample text." },
      ],
    },
    approach: {
      eyebrow: "My approach",
      title: "An approach\nthat looks like you.",
      lead: "Describe the principles that guide your practice. This is sample text — replace it from your dashboard.",
      pillars: [
        { title: "First focus area", body: "Name an area you cover and describe it in one sentence." },
        { title: "Second focus area", body: "Name an area you cover and describe it in one sentence." },
        { title: "Third focus area", body: "Name an area you cover and describe it in one sentence." },
        { title: "Fourth focus area", body: "Name an area you cover and describe it in one sentence." },
      ],
    },
    services: {
      eyebrow: "Services",
      title: "Care built\naround you.",
      lead: "Every meeting is a conversation. Describe how you work with people here. Replace this sample text.",
      items: [
        { tag: "Sample", title: "Discovery call", duration: "20 min", body: "Describe this first conversation: what you cover, and what the person leaves with. Replace this sample text." },
        { tag: "Sample", title: "Initial consultation", duration: "75 min", body: "Describe how a full first consultation unfolds. Replace this sample text." },
        { tag: "Sample", title: "Follow-up session", duration: "45 min", body: "Describe how you support people over time. Replace this sample text." },
      ],
      cta: "See availability",
    },
    testimonials: {
      eyebrow: "Testimonials",
      readOnGoogle: "Read on Google ↗",
      title: "What your clients\nsay about you.",
      items: [
        { quote: "The testimonials you publish from your dashboard will appear here, in place of this sample.", name: "Sample testimonial" },
        { quote: "Add your clients' own words: nothing reassures a hesitant visitor better than they do.", name: "Sample testimonial" },
        { quote: "Three testimonials are enough to start. You can add as many as you like.", name: "Sample testimonial" },
      ],
    },
    blog: {
      eyebrow: "The journal",
      title: "Your journal,\nyour subjects.",
      lead: "Publish your articles from your dashboard. Here is what they will look like.",
      items: [
        { tag: "Sample", title: "The title of your first article", excerpt: "The articles you publish will replace these samples. Write them from the Journal section of your dashboard.", date: "Coming soon" },
        { tag: "Sample", title: "The title of your second article", excerpt: "A short summary makes people want to read on: two sentences are enough.", date: "Coming soon" },
        { tag: "Sample", title: "The title of your third article", excerpt: "Write about the questions your clients ask you most often.", date: "Coming soon" },
      ],
      readMore: "Read article",
      viewAll: "View all articles",
    },
    contact: {
      eyebrow: "Let's stay in touch",
      title: "A question,\na step toward you.",
      lead: "Leave me a word. I personally answer every message, usually within 48 hours.",
      email: "Email",
      // See the fr block's comment: the ONE contact string not drawn from the backend's
      // `cfg.ui` locale layer, because it is shown when the fetch that would carry `cfg`
      // itself fails.
      loadError: "The contact form could not be loaded. Please email us directly instead.",
      placeLabel: "Location",
      info: {
        phone: "+1 555-0100",
        email: "contact@example.com",
        place: "Your city, your region",
      },
    },
    footer: {
      // No default either — same reasoning as hero.eyebrow/stat*/credential above.
      tagline: "",
      rights: "All rights reserved.",
      built: "Crafted with care.",
      navigate: "Navigate",
      explore: "Explore",
      social: "Social",
    },
    marquee: ["Wellbeing", "Balance", "Vitality", "Listening", "Prevention", "Energy", "Sleep", "Calm"],
    chatbot: {
      ariaLabel: "Virtual assistant",
      title: "AI Assistant",
      subtitle: "Answers in seconds",
      placeholder: "Type your question...",
      error: "Sorry, something went wrong. Please try again in a moment.",
    },
  },
  es: {
    nav: {
      story: "Mi historia",
      approach: "Enfoque",
      services: "Servicios",
      testimonials: "Testimonios",
      blog: "Diario",
      contact: "Contacto",
      book: "Reservar consulta",
      account: "Mi espacio",
      programs: "Programas",
      pathways: "Itinerarios",
      shop: "Tienda",
      reserve: "Reservar",
      courses: "Cursos",
    },
    hero: {
      // No default either — same reasoning as the en/fr blocks above. es also has no
      // stat1/stat1Label/stat2/stat2Label/stat3/stat3Label keys of its own; with the en
      // defaults now empty too, the deepMerge fallback for the missing es keys resolves to
      // empty rather than raw English filler, so the gap stops mattering.
      eyebrow: "",
      title: "Un acompañamiento\nhacia tu bienestar",
      lead: "Presenta aquí tu práctica y las personas a las que acompañas. Este texto es un ejemplo: reemplázalo desde tu panel.",
      ctaPrimary: "Reservar una llamada",
      ctaSecondary: "Descubrir mis servicios",
      credential: "",
    },
    story: {
      chapterWord: "capítulo",
      quote: "«Añade aquí una cita que resuma tu filosofía.»",
    },
    contact: {
       eyebrow: "Sigamos en contacto",
      title: "Una pregunta,\nun paso hacia ti.",
      lead: "Déjame unas palabras. Respondo personalmente cada mensaje, normalmente en menos de 48 horas.",
      email: "Correo",
      // See the fr block's comment: the ONE contact string not drawn from the backend's
      // `cfg.ui` locale layer, because it is shown when the fetch that would carry `cfg`
      // itself fails.
      loadError: "No se pudo cargar el formulario de contacto. Escríbenos directamente por correo electrónico.",
      placeLabel: "Ubicación",
      info: {
        phone: "+1 555-0100",
        email: "contact@example.com",
        place: "Tu ciudad, tu región",
      },
    },
    footer: {
      // No default either — same reasoning as the en/fr blocks above.
      tagline: "",
      rights: "Todos los derechos reservados.",
      built: "Elaborado con cuidado.",
      navigate: "Navegar",
      explore: "Descubrir",
      social: "Redes sociales",
    },
    marquee: ["Bienestar", "Equilibrio", "Vitalidad", "Escucha", "Prevención", "Energía", "Sueño", "Calma"],
    chatbot: {
      ariaLabel: "Asistente virtual",
      title: "Asistente IA",
      subtitle: "Respuestas en segundos",
      placeholder: "Escribe tu pregunta...",
      error: "Lo sentimos, ocurrió un error. Inténtalo de nuevo en un momento.",
    },
  },
};
